import Loading from "components/Loading";
import ActionsDropdown from "components/actions-dropdown/ActionsDropdown";
import ConfirmModal from "components/confirm-modal";
import { capitalize } from "lodash";
import { useEffect, useState } from "react";
import { Button, Dropdown, Table } from "react-bootstrap";
import { Link, NavLink } from "react-router-dom";
import querySubPaymentMethods from "services/subscription/querySubPaymentMethods";
import { AmplitudePageName, track } from "utils/amplitude";
import { fetchOrchidAPI } from "utils/api";
import { toDateString } from "./utils";
import { showIntercom } from "intercom";
import styles from "./SubscriptionSetting.module.scss";

//table options
const Options = ({
  subState,
  amplitudePageName,
  setShowConfirmCancel,
}: {
  subState: SubscriptionState;
  amplitudePageName: AmplitudePageName;
  setShowConfirmCancel?: (val: boolean) => void;
}) => {
  return (
    <ActionsDropdown drop={"down"}>
      <Dropdown.Item
        eventKey="1"
        as={NavLink}
        to={`/pro-dashboard/subscription-settings/${subState.subscription_type_suid}/update-payment`}
        onClick={() =>
          track(
            amplitudePageName,
            "update-payment-method-dropdown-option",
            "click",
          )
        }
      >
        Update Payment Method
      </Dropdown.Item>

      <Dropdown.Item
        eventKey="2"
        as={NavLink}
        to={`/pro-dashboard/subscription-settings/${subState.subscription_type_suid}/change-plan`}
        onClick={() =>
          track(amplitudePageName, "change-plan-dropdown-option", "click")
        }
      >
        Change Plan
      </Dropdown.Item>

      <Dropdown.Item
        eventKey="3"
        // as={NavLink}
        // to={`/pro-dashboard/subscription-settings/${subState.subscription_type_suid}/cancel`}
        onClick={() => {
          setShowConfirmCancel && setShowConfirmCancel(true);
          track(amplitudePageName, "cancel-dropdown-option", "click");
        }}
      >
        Cancel
      </Dropdown.Item>
    </ActionsDropdown>
  );
};

interface Props {
  amplitudePageName: AmplitudePageName;
}

const SubscriptionSetting: React.FC<Props> = ({ amplitudePageName }) => {
  const [subStates, setSubStates] = useState<Array<SubscriptionState>>([]);
  const [paymentList, setPaymentList] = useState<PaymentList[] | null>(null);

  //flag on loading subscription
  const [isLoading, setIsLoading] = useState(false);

  //status of subscription
  const getStatus = (subState: SubscriptionState, i: number) => {
    if (!subState) return "-";
    // if (subState.status === "scheduled") return "Current";
    // if (subStates[i - 1] && subStates[i - 1].status === "scheduled")
    //   return "Canceled";
    // if (subState.status === "active") return "Current";
    return capitalize(subState.status);
  };

  const enableActionDropdown = (subState: SubscriptionState, i: number) => {
    if (!subState) return false;
    if (subState.status === "scheduled") return true;
    if (subStates[i - 1] && subStates[i - 1].status === "scheduled")
      return false;
    if (subState.status === "active") return true;
    return false;
  };

  const validFrom = (subState: SubscriptionState) => {
    if (!subState) return "-";
    return subState.last_payment
      ? toDateString(new Date(subState.last_payment))
      : "-";
  };

  const validTo = (subState: SubscriptionState) => {
    if (!subState) return "-";
    if (subState.status === "scheduled" && !subState.last_payment) return "-";
    return subState.next_payment
      ? toDateString(new Date(subState.next_payment))
      : "-";
  };

  const validThrough = (subState: SubscriptionState, i: number) => {
    if (!subState.valid_through_end) {
      if (subStates[i - 1]?.valid_through_start) {
        return `${toDateString(
          new Date(subState.valid_through_start),
        )}-${toDateString(new Date(subStates[i - 1].valid_through_start))}`;
      } else {
        if (!subState.last_payment) {
          // it is likely a downgrade, old plan does not have last_payment
          const d = new Date(subState.valid_through_start);
          if (subState.subscription_type_period === "month") {
            d.setMonth(d.getMonth() + 1);
          } else {
            d.setFullYear(d.getFullYear() + 1);
          }
          return `${toDateString(
            new Date(subState.valid_through_start),
          )}-${toDateString(d)}`;
        } else {
          // in an upgrade, BE returns valid_through_start of the old state, new state needs to use last_payment instead
          const d = new Date(subState.last_payment);
          if (subState.subscription_type_period === "month") {
            d.setMonth(d.getMonth() + 1);
          } else {
            d.setFullYear(d.getFullYear() + 1);
          }
          return `${toDateString(
            new Date(subState.last_payment),
          )}-${toDateString(d)}`;
        }
      }
    }
    return `${toDateString(
      new Date(subState.valid_through_start),
    )}-${toDateString(new Date(subState.valid_through_end))}`;
  };

  const paymentInfo = (subState: SubscriptionState) => {
    if (!paymentList) return "-";
    const findOnePayment = paymentList.find((payment) => {
      return payment.id === subState.payment_method;
    });
    if (!findOnePayment) return "-";
    return `${capitalize(findOnePayment.card.brand)} ending in ${
      findOnePayment.card.last4
    }`;
  };

  useEffect(() => {
    const abortController = new AbortController();

    (async () => {
      try {
        setIsLoading(true);
        const response = await fetchOrchidAPI(
          "/api/subscriptions/v1/subscriptions/states/read",
          {
            method: "GET",
            signal: abortController.signal,
          },
        );

        const json = await response.json();
        //create map to ensure list is unique
        const mappedSubStates = new Map();

        for (const subState of json.states) {
          if (!mappedSubStates.has(subState.subscription_type_suid)) {
            mappedSubStates.set(subState.subscription_type_suid, [subState]);
          } else {
            mappedSubStates.set(subState.subscription_type_suid, [
              ...mappedSubStates.get(subState.subscription_type_suid),
              subState,
            ]);
          }
        }

        // filter out chechkout_abandoned subStates
        const subStates = [...mappedSubStates]
          .flat(Infinity)
          .filter(
            (el) =>
              typeof el !== "string" &&
              el.terminated_code !== "checkout_abandoned",
          );

        setSubStates(subStates);
      } catch (error: any) {
        if (error.name === "AbortError") return;
      }
    })();

    querySubPaymentMethods().then((val) => {
      if (abortController.signal.aborted) return;
      setPaymentList(val);
      setIsLoading(false);
    });

    return () => {
      abortController.abort();
    };
  }, []);

  const [showConfirmCancel, setShowConfirmCancel] = useState(false);

  return isLoading ? (
    <Loading />
  ) : (
    <div className={styles["setting__subscription"]}>
      {showConfirmCancel && subStates.length > 0 && (
        <ConfirmModal
          show={showConfirmCancel && subStates.length > 0}
          onHide={() => setShowConfirmCancel(false)}
          onConfirm={() => {}}
          onCancel={() => setShowConfirmCancel(false)}
          labelTitle="Canceling Your Plan? 😢"
          footerClass={"d-none"}
        >
          <p>
            Before you cancel, we have a few quick questions to help us improve
            our service. Your insights will allow us to build something
            clinicians need.
          </p>
          <p>
            Thank you for helping us get better, and we are sorry to see you go!
          </p>
          <Button className={"w-100 mb-2"} onClick={() => showIntercom()}>
            Chat with us
          </Button>
          <Button
            className={"w-100 mb-2"}
            variant={"outline-primary"}
            onClick={() => setShowConfirmCancel(false)}
          >
            Keep My Plan
          </Button>
          <Button
            className={"w-100 gray01"}
            variant={"link"}
            as={Link as any}
            to={`/pro-dashboard/subscription-settings/${subStates[0]?.subscription_type_suid}/cancel`}
          >
            Continue to Cancel
          </Button>
        </ConfirmModal>
      )}
      <h3>Subscription Plans</h3>
      <Table>
        <thead>
          <tr>
            <th>Subscription Type</th>
            <th>Status</th>
            <th>Valid through</th>
            <th>Last Payment</th>
            <th>Next Payment Due</th>
            <th>Payment Method</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {subStates.length ? (
            subStates.map((subState, i) => (
              <tr key={subState.subscription_type_suid + i}>
                <td>
                  <div>
                    <b>
                      {subState.subscription_type_class === "core-with-ai"
                        ? "Core with AI Toolkit"
                        : `${capitalize(
                            subState.subscription_type_class,
                          )} Plan`}
                    </b>
                    <span>
                      {subState.subscription_type_period === "month"
                        ? "Monthly"
                        : "Annual"}{" "}
                      Plan
                    </span>
                  </div>
                </td>
                <td>{getStatus(subState, i)}</td>
                <td>{validThrough(subState, i)}</td>
                <td>
                  <div>
                    <span>{validFrom(subState)}</span>
                    {/* <Link to={"/"}>view invoice</Link> */}
                  </div>
                </td>
                <td>{validTo(subState)}</td>
                <td>{paymentInfo(subState)}</td>
                <td>
                  {enableActionDropdown(subState, i) && (
                    <Options
                      subState={subState}
                      amplitudePageName={amplitudePageName}
                      setShowConfirmCancel={setShowConfirmCancel}
                    />
                  )}
                </td>
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan={7}>
                <section className={styles["table-no-content"]}>
                  <span className="rg-16 gray02">
                    No Subscription plan yet.
                  </span>
                  <Link to="/pricing">
                    <Button
                      variant="outline-primary"
                      onClick={() =>
                        track(amplitudePageName, "shop-plans-button", "click")
                      }
                    >
                      Shop Plans
                    </Button>
                  </Link>
                </section>
              </td>
            </tr>
          )}
        </tbody>
      </Table>
    </div>
  );
};

export default SubscriptionSetting;
